import React, { useState, useEffect } from 'react';
import DashboardLayout from '../../components/layout/DashboardLayout';

/**
 * Trainer Help Page
 * Provides step-by-step instructions for trainers on how to set up and manage sessions
 * Shows different content for individual/solo trainers vs normal trainers
 */

const TrainerGuidancePage = () => {
  const [activeSection, setActiveSection] = useState('before-session');
  const [isSolo, setIsSolo] = useState(false);

  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      const parsedUser = JSON.parse(userData);
      const org = parsedUser.primaryOrganization || parsedUser.organization;
      setIsSolo(org?.type === 'individual');
    }
  }, []);

  const sections = [
    { id: 'before-session', name: 'Before Session' },
    { id: 'during-session', name: 'During Session' },
    { id: 'after-session', name: 'After Session' },
  ];

  // Step card component
  const StepCard = ({ number, title, description, tips }) => (
    <div className="bg-white border border-neutral-200 rounded-xl p-5 hover:shadow-md transition-shadow">
      <div className="flex items-start gap-4">
        <div className="w-10 h-10 bg-primary-100 text-primary-700 rounded-full flex items-center justify-center font-bold text-base sm:text-lg flex-shrink-0">
          {number}
        </div>
        <div className="flex-1">
          <h3 className="text-base sm:text-lg font-semibold text-neutral-900 mb-2">{title}</h3>
          <p className="text-sm text-neutral-600 mb-3">{description}</p>
          {tips && tips.length > 0 && (
            <div className="bg-amber-50 rounded-lg p-3 border border-amber-200">
              <p className="text-xs font-semibold text-amber-800 mb-1">Tips:</p>
              <ul className="text-xs text-amber-700 space-y-1">
                {tips.map((tip, index) => (
                  <li key={index}>• {tip}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <DashboardLayout>
      <div className="p-4 lg:p-6 pb-20 lg:pb-6">
        <div className="max-w-4xl mx-auto">
          {/* Page Header */}
          <div className="mb-8">
            <h1 className="text-2xl lg:text-3xl font-bold text-neutral-900 mb-2">
              Help
            </h1>
            <p className="text-neutral-600">
              {isSolo
                ? 'Step-by-step instructions to help you create, manage, and run your training sessions independently.'
                : 'Step-by-step instructions to help you set up and manage your training sessions effectively.'}
            </p>
          </div>

          {/* Quick Overview Card */}
          <div className="bg-gradient-to-r from-primary-50 to-blue-50 rounded-xl p-6 mb-8 border border-primary-200">
            <div className="flex items-start gap-3">
              <svg className="w-6 h-6 text-primary-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              <div>
                <h2 className="text-base sm:text-lg font-semibold text-primary-900 mb-2">
                  {isSolo ? 'Independent Trainer Overview' : 'Trainer Responsibilities Overview'}
                </h2>
                {isSolo ? (
                  <ul className="text-sm text-primary-800 space-y-1">
                    <li>• Create sessions with name, date, time, and participant settings</li>
                    <li>• Add sub-sessions and activities to organize your training</li>
                    <li>• Start the session and share the code/QR with participants</li>
                    <li>• Monitor participant engagement during live sessions</li>
                    <li>• Review session reports, feedback, and your performance metrics</li>
                    <li>• Manage your subscription and billing from the Billing page</li>
                  </ul>
                ) : (
                  <ul className="text-sm text-primary-800 space-y-1">
                    <li>• Your client creates sessions and assigns them to you</li>
                    <li>• Create sub-sessions and activities before the session starts</li>
                    <li>• Review participant challenges and personalization responses</li>
                    <li>• Start the session at the scheduled time</li>
                    <li>• Monitor participant engagement during live sessions</li>
                    <li>• Review session reports and participant feedback</li>
                  </ul>
                )}
              </div>
            </div>
          </div>

          {/* Section Navigation */}
          <div className="flex flex-wrap gap-2 mb-8">
            {sections.map((section) => (
              <button
                key={section.id}
                onClick={() => setActiveSection(section.id)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  activeSection === section.id
                    ? 'bg-primary-500 text-white'
                    : 'bg-white border border-neutral-200 text-neutral-700 hover:bg-neutral-50'
                }`}
              >
                {section.name}
              </button>
            ))}
          </div>

          {/* Before Session Section */}
          {activeSection === 'before-session' && (
            <div className="space-y-4">
              <div className="bg-blue-50 rounded-xl p-4 mb-6 border border-blue-200">
                <h2 className="text-base sm:text-lg font-semibold text-blue-900 mb-2">Before the Session Starts</h2>
                <p className="text-sm text-blue-800">
                  {isSolo
                    ? 'As an independent trainer, you create and set up your own sessions. Follow these steps to get your session ready for participants.'
                    : 'Complete these steps to prepare your session for participants. A well-prepared session leads to better engagement and outcomes.'}
                </p>
              </div>

              {isSolo && (
                <StepCard
                  number={1}
                  title="Create a New Session"
                  description="Go to 'My Sessions' from the sidebar and click 'Create Session'. Fill in the session name, description, date, time, and expected number of participants."
                  tips={[
                    "Set a clear session name that participants will see when joining",
                    "Choose the correct date and time in your timezone",
                    "Set expected participants — this is the maximum who can join",
                    "You can upload workbooks (PDF/PPT) for participants to access"
                  ]}
                />
              )}

              <StepCard
                number={isSolo ? 2 : 1}
                title={isSolo ? "Open Your Session" : "Access Your Session"}
                description={isSolo
                  ? "After creating, go to 'My Sessions' and click on your session to view its details. From there, click 'Setup' to configure sub-sessions and activities."
                  : "Go to 'My Sessions' from the sidebar and find the session assigned to you by the client. Click on the session to view its details."}
                tips={[
                  "Sessions are organized by date with upcoming sessions shown first",
                  "You can use the search or filter options to find specific sessions"
                ]}
              />

              <StepCard
                number={isSolo ? 3 : 2}
                title="Create Sub-Sessions (Topics)"
                description="Add sub-sessions to organize your training into different topics or modules. Each sub-session can have its own set of activities."
                tips={[
                  "Give each sub-session a clear, descriptive name",
                  "Consider the logical flow of topics when ordering sub-sessions",
                  "You can reorder sub-sessions by dragging them"
                ]}
              />

              <StepCard
                number={isSolo ? 4 : 3}
                title="Add Activities to Sub-Sessions"
                description="For each sub-session, add activities such as challenges, action plans, polls, feedback forms, and more. Configure each activity with appropriate settings."
                tips={[
                  "Use 'Challenge' activities to gather participant pain points",
                  "Use 'Action Plan' activities to help participants commit to actions",
                  "Use 'Feedback' activities to collect participant feedback",
                  "Enable timers for time-bound activities"
                ]}
              />

              <StepCard
                number={isSolo ? 5 : 4}
                title="Configure Activity Settings"
                description="For each activity, set the title, instructions, and whether a timer should be enabled. Preview activities to see how they'll appear to participants."
                tips={[
                  "Keep instructions clear and concise",
                  "Use appropriate duration for timed activities",
                  "Preview activities before saving"
                ]}
              />

              <StepCard
                number={isSolo ? 6 : 5}
                title="Save Your Changes"
                description="Click 'Save Changes' to save your session setup. Make sure all required activities are added before the session starts."
                tips={[
                  "Always save before leaving the setup page",
                  "You'll see a warning if you try to leave without saving",
                  "Review your setup one final time before the session"
                ]}
              />

              <StepCard
                number={isSolo ? 7 : 6}
                title="Review Participant Responses"
                description="Before the session, check if participants have submitted any pre-session challenges or personalization responses. Use this information to tailor your training."
                tips={[
                  "Participant challenges help you understand their pain points",
                  "Personalization responses help you customize content",
                  "Address common challenges during the session"
                ]}
              />
            </div>
          )}

          {/* During Session Section */}
          {activeSection === 'during-session' && (
            <div className="space-y-4">
              <div className="bg-green-50 rounded-xl p-4 mb-6 border border-green-200">
                <h2 className="text-base sm:text-lg font-semibold text-green-900 mb-2">During the Session</h2>
                <p className="text-sm text-green-800">
                  Follow these steps to run your session effectively and keep participants engaged throughout.
                </p>
              </div>

              <StepCard
                number={1}
                title="Start the Session"
                description="Click the 'Start Session' button when ready to begin. This will mark the session as 'Live' and allow participants to join."
                tips={[
                  "Start at the scheduled time for best attendance",
                  "You'll see a confirmation before the session starts",
                  "Participants can join once the session is active"
                ]}
              />

              <StepCard
                number={2}
                title="Share Session Code"
                description="Share the unique session code with participants so they can join the session. You can display a QR code or share the code directly."
                tips={[
                  "Display the QR code on screen for easy joining",
                  "Participants can enter the code manually at the join page",
                  "The code remains active throughout the session"
                ]}
              />

              <StepCard
                number={3}
                title="Launch Activities"
                description="Use the session control panel to launch activities one at a time. Participants will see the current activity on their screens."
                tips={[
                  "Launch activities in the planned sequence",
                  "Allow enough time for participants to respond",
                  "You can see real-time response counts"
                ]}
              />

              <StepCard
                number={4}
                title="Monitor Engagement"
                description="Track participant responses in real-time. The session control panel shows how many participants have responded to each activity."
                tips={[
                  "Encourage participation if response rates are low",
                  "Address questions or confusion promptly",
                  "Use the participant list to track individual engagement"
                ]}
              />

              <StepCard
                number={5}
                title="Review Responses Live"
                description="View participant responses as they come in. Use interesting responses to spark discussions and make the session interactive."
                tips={[
                  "Highlight insightful responses during the session",
                  "Address common themes in challenges",
                  "Provide feedback on action plans"
                ]}
              />

              <StepCard
                number={6}
                title="Move Between Sub-Sessions"
                description="Navigate between sub-sessions as you progress through the training. Each sub-session's activities become available when you activate it."
                tips={[
                  "Complete all activities before moving to next sub-session",
                  "Summarize key points before transitioning",
                  "Keep an eye on the time"
                ]}
              />

              <StepCard
                number={7}
                title="End the Session"
                description="When all activities are complete, click 'End Session' to mark it as completed. This will finalize all participant responses."
                tips={[
                  "Ensure all participants have submitted their responses",
                  "Thank participants before ending",
                  "The session report becomes available after ending"
                ]}
              />
            </div>
          )}

          {/* After Session Section */}
          {activeSection === 'after-session' && (
            <div className="space-y-4">
              <div className="bg-purple-50 rounded-xl p-4 mb-6 border border-purple-200">
                <h2 className="text-base sm:text-lg font-semibold text-purple-900 mb-2">After the Session</h2>
                <p className="text-sm text-purple-800">
                  Complete these post-session tasks to maximize the value of your training and improve future sessions.
                </p>
              </div>

              <StepCard
                number={1}
                title="View Session Report"
                description="Access the session report to see comprehensive metrics including engagement rates, feedback scores, and participant responses."
                tips={[
                  "Reports are available immediately after session ends",
                  "Review key metrics like TEI, TRS, ARS, and LAI",
                  "Export reports for sharing with stakeholders"
                ]}
              />

              <StepCard
                number={2}
                title="Review Participant Feedback"
                description="Read through individual participant feedback and identify areas for improvement. Look for patterns in the responses."
                tips={[
                  "Pay attention to constructive criticism",
                  "Note what worked well for future sessions",
                  "Follow up on specific participant concerns if needed"
                ]}
              />

              <StepCard
                number={3}
                title="Analyze Action Plans"
                description="Review the action plans participants created. These show their commitment to applying what they learned."
                tips={[
                  "Check ARS scores to gauge action readiness",
                  "Identify common action themes",
                  "Consider follow-up activities to support implementation"
                ]}
              />

              <StepCard
                number={4}
                title="Check Your Performance Metrics"
                description="Visit the Performance page to see your aggregated metrics across all sessions. Track your improvement over time."
                tips={[
                  "Monitor your TEI (Trainer Effectiveness Index)",
                  "Compare metrics across different sessions",
                  "Set goals for improvement"
                ]}
              />

              {isSolo ? (
                <StepCard
                  number={5}
                  title="Duplicate or Create Next Session"
                  description="Use the duplicate feature to quickly create a similar session with the same sub-sessions and activities, or create a brand new session from scratch."
                  tips={[
                    "Duplicating saves time by copying all sub-sessions and activities",
                    "Update the date, time, and name for the duplicated session",
                    "Review and adjust activities based on feedback from previous sessions"
                  ]}
                />
              ) : (
                <StepCard
                  number={5}
                  title="Share Reports with Client"
                  description="If applicable, share the session report with the client organization. They can access group-level insights and metrics."
                  tips={[
                    "Reports can be exported as PDF",
                    "Highlight key achievements and areas of focus",
                    "Include recommendations for follow-up"
                  ]}
                />
              )}

              <StepCard
                number={6}
                title="Prepare for Next Session"
                description="Use insights from this session to improve your next training. Update your materials based on feedback and common challenges."
                tips={[
                  "Document lessons learned",
                  "Update activity configurations if needed",
                  "Consider adding new activities based on feedback"
                ]}
              />
            </div>
          )}

          {/* Help Section */}
          <div className="mt-8 bg-neutral-100 rounded-xl p-6 border border-neutral-200">
            <div className="flex items-start gap-3">
              <svg className="w-6 h-6 text-neutral-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div>
                <h3 className="font-semibold text-neutral-900 mb-2">Need Help?</h3>
                <p className="text-sm text-neutral-600 mb-3">
                  {isSolo
                    ? 'If you have questions or encounter any issues, reach out to our support team.'
                    : 'If you have questions or encounter any issues, please contact your organization administrator or reach out to our support team.'}
                </p>
                <div className="flex flex-wrap gap-2">
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-white border border-neutral-300 text-neutral-700">
                    support@aktivity.com
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default TrainerGuidancePage;
